import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import InstallTip from "@/components/InstallTip";

export default function LoginPage() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(false);
  const [show, setShow] = useState(false);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  async function submit(e) {
    e.preventDefault();
    setErr("");
    if (!email.trim() || !password) { setErr("Email and password are required"); return; }
    setBusy(true);
    try {
      const user = await login(email.trim(), password, remember);
      if (user.must_change_password) nav("/change-password", { replace: true });
      else if (user.role === "Worker") nav("/my/timesheets", { replace: true });
      else nav("/", { replace: true });
    } catch (e) {
      setErr(e?.response?.data?.detail || "Invalid credentials");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-black p-4" data-testid="login-page">
      <div className="w-full max-w-md space-y-3">
      <InstallTip />
      <form onSubmit={submit} className="w-full max-w-md card" autoComplete="off">
        <div className="flex items-center justify-center mb-6">
          <div className="border-2 border-silver px-6 py-3 text-silver tracking-widest font-bold">
            LW&amp;Co <span className="text-white">Hub</span>
          </div>
        </div>
        <h1 className="text-xl text-white mb-6 text-center">Sign in</h1>
        <label className="block text-sm text-silver mb-1">Email</label>
        <input data-testid="login-email" className="input mb-4" type="email"
               autoComplete="off" value={email} onChange={(e) => setEmail(e.target.value)} placeholder=""/>
        <label className="block text-sm text-silver mb-1">Password</label>
        <div className="relative mb-4">
          <input data-testid="login-password" className="input pr-10"
                 type={show ? "text" : "password"} autoComplete="new-password"
                 value={password} onChange={(e) => setPassword(e.target.value)}/>
          <button type="button" data-testid="login-password-toggle" onClick={() => setShow(s => !s)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-silver"
                  aria-label="Toggle password visibility">
            {show ? <EyeOff size={18}/> : <Eye size={18}/>}
          </button>
        </div>
        <label className="flex items-center gap-2 mb-4 text-sm text-silver cursor-pointer" data-testid="login-remember-label">
          <input type="checkbox" checked={remember} onChange={e => setRemember(e.target.checked)}
                 data-testid="login-remember"/>
          <span>Keep me signed in for 30 days on this device</span>
        </label>
        {err && <div data-testid="login-error" className="text-red-500 text-sm mb-3">{err}</div>}
        <button data-testid="login-submit" disabled={busy} className="btn-silver w-full">
          {busy ? "Signing in…" : "Sign in"}
        </button>
        <p className="text-xs opacity-50 mt-4 text-center">
          Unticked: session ends when you close the browser or after 12 hours idle.
        </p>
      </form>
      </div>
    </div>
  );
}
